"use client";

import React from "react";
import { ConnectionCardProps } from "@/components/tailwind/ui/ConnectionCard";
import type { ConnectorListing } from "@/hooks/integrations";

export interface Connection extends Omit<ConnectionCardProps, "icon" | "iconOverlay"> {
  id: string;
  /** Bespoke mark. Left unset, the id resolves against lib/connector-icons. */
  icon?: React.ReactNode;
  iconOverlay?: React.ReactNode;
  showPlusIcon?: boolean;
  isConnected?: boolean;
  connectUrl?: string;
  category?: string;
  /** Marketplace detail-page content from the backend catalog (may be absent on static fallback). */
  listing?: ConnectorListing;
  /**
   * Auth model from the catalog. "none" marks embed connectors, which have
   * no OAuth flow — their cards open the detail page instead of a connect
   * popup (a Connect button there would do nothing).
   */
  authKind?: string;
}

// Only integrations with real backend support are listed here. Adding a card
// without wiring the matching connect/disconnect routes in IntegrationsContent's
// handleConnectionAction would show a Connect button that does nothing.
export const AVAILABLE_CONNECTIONS: Connection[] = [
  {
    id: "github",
    name: "GitHub",
    description: "View the latest updates from GitHub in Closot pages and databases",
    // "Sync" was never backed by anything — no GitHub synced-database
    // provider exists. This array is only the fallback shown while the
    // backend catalog loads or if it fails, so it has to tell the same
    // truth the catalog does. Source of truth is the connector manifest.
    badges: ["Link Preview", "PR Status"],
    actionButtonText: "Connect",
    actionButtonVariant: "connect",
    category: "development",
  },
  {
    id: "slack",
    name: "Slack",
    description: "Notifications, live links, and workflows between Closot and Slack",
    badges: ["Link Preview"],
    actionButtonText: "Connect",
    actionButtonVariant: "connect",
    category: "communication",
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    description: "Auto-schedule meeting bots from your calendar events with meeting links",
    badges: ["Auto-Schedule", "Real-time Sync"],
    actionButtonText: "Connect",
    actionButtonVariant: "connect",
    category: "productivity",
  },
  {
    // Mirrors the googleDrive manifest, for when the catalog request fails.
    id: "google-drive",
    name: "Google Drive",
    description: "Preview Drive files, Docs, Sheets and Slides inside your pages",
    badges: ["Link Preview"],
    actionButtonText: "Connect",
    actionButtonVariant: "connect",
    category: "storage",
  },
  {
    id: "gmail",
    name: "Gmail",
    description: "Let agents read, draft, send, and label emails on your behalf",
    badges: ["Read", "Send", "Labels"],
    actionButtonText: "Connect",
    actionButtonVariant: "connect",
    category: "communication",
  },
];
